import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { LibraryBig, Loader2, Sparkles, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  KnowledgebaseFilterProvider,
  useKnowledgebaseFilters,
} from "@/contexts/KnowledgebaseFilterContext";
import { KnowledgebaseLayout } from "@/components/portal/shared/KnowledgebaseLayout";
import { RagAnswer } from "@/components/portal/shared/RagAnswer";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import {
  ragApi,
  pollUntilComplete,
  type QueryResponse,
  type QueryJobStatusResponse,
} from "@/services/api/rag";
import { QueryProgress } from "@/components/portal/QueryProgress";

export const Route = createFileRoute("/portal/catalogue-intelligence")({
  component: CatalogueIntelligencePage,
});

const EXAMPLE_PROMPTS = [
  "Which projector is best for a bright lounge we watch sport in during the day?",
  "Compare the Ring Battery Doorbell Plus and the Gen 2 video doorbell",
  "Best robot vacuum for a large home with two dogs",
  "What is our marketing support process from brands?",
];

function sourceLabel(s: Record<string, unknown>): string | null {
  const meta = s.metadata as Record<string, unknown> | undefined;
  const title = (s.document_title ?? meta?.document_title) as string | undefined;
  return title || null;
}

// Route component: provide the shared knowledgebase filter context + sidebar
// (categories/labels/version multi-select) around the ask UI. Gated by the
// `rag`/`kb-file` service entitlement (same as Knowledgebase File) — available
// to any org with the knowledge base, not just platform admins.
function CatalogueIntelligencePage() {
  const { user, isLoading } = useAuth();
  const { isOwner, role } = usePermissions();
  const navigate = useNavigate();
  const isPlatformAdmin = user?.is_admin === true;

  // TEMPORARY lockdown: until Catalogue Intelligence has its own subscription
  // gate it rides on the `rag` entitlement, so it must not be reachable by
  // non-owners. Sidebar visibility is handled via excludeRoles in
  // navigationItems.ts; this guards direct URL navigation. Once subscription-
  // gated, remove this guard.
  const allowed = isPlatformAdmin || isOwner;

  useEffect(() => {
    // Wait until auth + the org role have resolved before deciding.
    if (isLoading || (!isPlatformAdmin && role == null)) return;
    if (!allowed) navigate({ to: "/portal" });
  }, [isLoading, isPlatformAdmin, role, allowed, navigate]);

  if (isLoading || (!isPlatformAdmin && role == null) || !allowed) {
    return null;
  }

  return (
    <KnowledgebaseFilterProvider>
      <Helmet>
        <title>Catalogue Intelligence | Larasoft</title>
      </Helmet>
      <KnowledgebaseLayout>
        <CatalogueAsk />
      </KnowledgebaseLayout>
    </KnowledgebaseFilterProvider>
  );
}

// Inner component: calls the RAG query API directly (not the agentic service —
// see the RAG-direct migration), scoping by the categories/labels the user
// picked, with temperature 0 for deterministic factual answers.
function CatalogueAsk() {
  const { token } = useAuth();
  const { filters } = useKnowledgebaseFilters();

  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  // Pipeline progress while the query runs; cleared on each new ask.
  const [progress, setProgress] = useState<QueryJobStatusResponse | null>(null);
  const [answer, setAnswer] = useState<QueryResponse | null>(null);

  const categoryCount = filters.selectedCategoryIds.length;
  const labelCount = filters.selectedLabelIds.length;
  const scopeLabel =
    categoryCount || labelCount
      ? [
          categoryCount ? `${categoryCount} categor${categoryCount === 1 ? "y" : "ies"}` : null,
          labelCount ? `${labelCount} label${labelCount === 1 ? "" : "s"}` : null,
        ]
          .filter(Boolean)
          .join(" + ")
      : null;

  async function handleAsk(q?: string) {
    const query = (q ?? question).trim();
    if (query.length < 3) {
      toast.error("Please enter a question.");
      return;
    }
    setQuestion(query);
    setLoading(true);
    setAnswer(null);
    setProgress(null);
    try {
      const enqueue = await ragApi.executeQuery(token ?? "", {
        query_text: query,
        category_ids: filters.selectedCategoryIds.length
          ? filters.selectedCategoryIds
          : undefined,
        label_ids: filters.selectedLabelIds.length
          ? filters.selectedLabelIds
          : undefined,
        latest_versions_only: filters.latestVersionsOnly,
        // Deterministic factual answers — RAG's default of 0.7 is for creative
        // generation; catalogue Q&A should be stable.
        temperature: 0,
      });
      // The `undefined`s take the defaults for interval/timeout/error
      // tolerance — passed explicitly rather than repeating their values, so a
      // change to the defaults still applies here.
      const status = await pollUntilComplete(
        () => ragApi.getQueryJobStatus(token ?? "", enqueue.job_id),
        (s) => s.status === "completed" || s.status === "failed",
        undefined,
        undefined,
        undefined,
        undefined,
        (s) => setProgress(s),
      );
      if (status.status === "failed") {
        throw new Error(status.error || "Query failed");
      }
      setAnswer(status.result ?? null);
    } catch (err) {
      toast.error(`Ask failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }

  const namedSources = (answer?.sources ?? [])
    .map(sourceLabel)
    .filter((l): l is string => Boolean(l));
  const uniqueSources = Array.from(new Set(namedSources));

  return (
    <div className="h-full overflow-y-auto p-4 md:p-6 space-y-4">
      <PageHeader
        title="Catalogue Intelligence"
        description="Ask anything about the catalogue and knowledge base — product questions, comparisons, recommendations, or process and policy. Use the Filters panel to scope your question to specific categories or labels. Answers are drawn from your ingested documents, with citations."
        icon={LibraryBig}
      />

      <Card>
        <CardHeader>
          <CardTitle>Ask the catalogue</CardTitle>
          <CardDescription>
            Plain English — e.g. &ldquo;best projector for a bright room&rdquo;, or
            &ldquo;compare the Ring battery doorbell and the Gen 2&rdquo;.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a question…  (⌘/Ctrl + Enter to send)"
            rows={3}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAsk();
            }}
          />
          {scopeLabel && (
            <p className="text-xs text-muted-foreground">
              Scoped to {scopeLabel} (change in the Filters panel).
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_PROMPTS.map((p) => (
              <Button
                key={p}
                variant="outline"
                size="sm"
                disabled={loading}
                onClick={() => handleAsk(p)}
              >
                <Sparkles className="mr-1 h-3 w-3" /> {p}
              </Button>
            ))}
          </div>
          <Button onClick={() => handleAsk()} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Asking…
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" /> Ask
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {loading && (
        <Card>
          <CardContent className="pt-6">
            <QueryProgress
              stage={progress?.stage}
              sources={progress?.sources}
            />
          </CardContent>
        </Card>
      )}

      {answer && (
        <Card>
          <CardHeader>
            <CardTitle>Answer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <RagAnswer
              content={answer.answer || "_No answer returned._"}
              citations={answer.citations}
            />
            {uniqueSources.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Sources</p>
                <div className="flex flex-wrap gap-2">
                  {uniqueSources.map((label) => (
                    <Badge key={label} variant="secondary">
                      {label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
